import React, { useEffect, useState } from 'react';
import {
  AngleInput,
  ButtonsGroup,
  ColorPickerInput,
  Composites,
  OpacityPicker,
  Slider,
  TextLabel,
  ToggleSwitch,
} from '@wix/wix-base-ui';
import {
  convertHexaOrHexToRGBAUnits,
  isHexColor,
  ControlTypes,
} from '@wix/editor-elements-common-utils';
import { usePrevious } from '@wix/editor-elements-preview-utils/preview';
import {
  DEFAULT_SHADOW,
  DataHooks,
  TranslationKeys,
} from '../../TextDesign/constants';
import type {
  Color,
  CssUnitValue,
  Shadow,
  ThemeColor,
} from '../../../adapters/types';
import {
  getCssColor,
  getColorString,
  isEqual,
  OpenColorPickerType,
  getAlpha,
} from '../../utils';
import type { SiteColorMap } from '../../../types';
import type { BiConfigType } from '../../types';
import styles from './ShadowInput.scss';

const getFallbackShadow = (unit = 'px') =>
  ({
    type: 'TextShadow',
    angle: 315,
    blur: {
      type: 'CssUnitValue',
      value: 2,
      unit,
    },
    distance: {
      type: 'CssUnitValue',
      value: 4,
      unit,
    },
    color: {
      type: 'Color',
      red: 0,
      green: 0,
      blue: 0,
      alpha: 1,
    },
  } as Shadow);

const SHADOW_INSET_OPTIONS = [
  { value: '', label: TranslationKeys.shadows.shadowDrop },
  { value: 'inset', label: TranslationKeys.shadows.shadowInner },
];

type ShadowProps = {
  shadow: Shadow | undefined;
  onChange: (value: any, config?: BiConfigType) => void;
  onChangeDebounced: (value: any, config?: BiConfigType) => void;
  openColorPicker: OpenColorPickerType;
  siteColorMap: SiteColorMap;
  layersMode?: boolean;
};

export const ShadowInput = (props: ShadowProps) => {
  const {
    shadow,
    onChange,
    onChangeDebounced,
    openColorPicker,
    siteColorMap,
    layersMode = false,
  } = props;
  const [showShadow, setShowShadow] = useState(layersMode);
  const [currentShadow, setCurrentShadow] = useState<Partial<Shadow>>(
    getFallbackShadow(),
  );

  const previousShadow = usePrevious(shadow) ?? shadow;

  const [isFirstOpening, setIsFirstOpening] = useState(true);
  const handleShadowPanelShow = () => {
    if (!showShadow) {
      let newShadow;
      if (isFirstOpening) {
        newShadow = getFallbackShadow();
        setCurrentShadow(newShadow);
        setIsFirstOpening(false);
      } else {
        newShadow = currentShadow;
      }
      onChange(
        { textShadow: newShadow },
        {
          controlId: 'apply_shadow',
          controlType: ControlTypes.radioButtons,
          newValue: true,
          prevValue: false,
        },
      );
    } else {
      onChange(
        {
          textShadow: {
            value: '0px 0px 0px transparent',
            type: 'CssValue',
          },
        },
        {
          controlId: 'apply_shadow',
          controlType: ControlTypes.radioButtons,
          newValue: false,
          prevValue: true,
        },
      );
    }
    setShowShadow(!showShadow);
  };

  useEffect(() => {
    if (shadow && !isEqual(shadow, DEFAULT_SHADOW)) {
      setShowShadow(true);
      setCurrentShadow(shadow);
      setIsFirstOpening(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShadowChange = (
    newShadow: Partial<Shadow>,
    config: BiConfigType,
    debounce = false,
  ) => {
    setCurrentShadow({ ...currentShadow, ...newShadow });
    const onChangeFunc = debounce ? onChangeDebounced : onChange;
    if (layersMode) {
      onChangeFunc({ ...currentShadow, ...newShadow }, config);
    } else {
      onChangeFunc({ textShadow: { ...currentShadow, ...newShadow } }, config);
    }
  };

  const handleShadowColorClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const onShadowColorChange = (newColorString: string) => {
      let newColor: Color | ThemeColor | undefined;
      if (isHexColor(newColorString)) {
        const [red, green, blue] =
          convertHexaOrHexToRGBAUnits(newColorString) || [];
        newColor = {
          type: 'Color',
          red,
          green,
          blue,
          alpha: getAlpha(currentShadow.color),
        } as Color;
      } else {
        newColor = {
          type: 'ThemeColor',
          theme: newColorString,
          overrides: {
            alpha: getAlpha(currentShadow.color),
            type: 'ThemeColorOverrides',
          },
        } as ThemeColor;
      }
      handleShadowChange(
        {
          color: {
            ...currentShadow?.color,
            ...newColor,
          },
        },
        {
          controlId: 'shadow_color',
          controlType: ControlTypes.input,
          newValue: newColorString,
          prevValue: getColorString(previousShadow?.color!),
        },
      );
    };
    openColorPicker(event, currentShadow?.color || '', onShadowColorChange);
  };

  const handleInsetChange = (value: string) => {
    const newShadow = {
      ...currentShadow,
      inset: Boolean(value),
    };
    handleShadowChange(
      newShadow,
      {
        controlId: 'shadow_inset',
        controlType: ControlTypes.slider,
        newValue: newShadow.inset,
        prevValue: previousShadow?.inset,
      },
      true,
    );
  };

  const handleOpacityChange = (newValue: number) => {
    const newShadow = {
      color: { ...currentShadow.color },
    } as Partial<Shadow>;
    const alpha = newValue / 100;
    if (currentShadow.color?.type === 'ThemeColor') {
      (newShadow.color as ThemeColor).overrides = {
        ...currentShadow.color.overrides!,
        alpha,
      };
    } else {
      newShadow.color = {
        ...currentShadow.color!,
        alpha,
      };
    }
    handleShadowChange(
      newShadow,
      {
        controlId: 'shadow_opacity',
        controlType: ControlTypes.slider,
        newValue,
        prevValue: (getAlpha(previousShadow?.color) || 0) * 100,
      },
      true,
    );
  };

  const handleAngleChange = (newValue: number) => {
    if (newValue === previousShadow?.angle) {
      return;
    }
    const newShadow = { angle: newValue } as Partial<Shadow>;
    handleShadowChange(
      newShadow,
      {
        controlId: 'shadow_angle',
        controlType: ControlTypes.input,
        newValue,
        prevValue: previousShadow?.angle,
      },
      true,
    );
  };

  const handleDistanceChange = (newValue: number) => {
    const newShadow = {
      distance: {
        ...currentShadow.distance,
        value: newValue,
      } as CssUnitValue,
    } as Partial<Shadow>;
    handleShadowChange(
      newShadow,
      {
        controlId: 'shadow_distance',
        controlType: ControlTypes.slider,
        newValue,
        prevValue: previousShadow?.distance?.value,
      },
      true,
    );
  };

  const handleBlurChange = (newValue: number) => {
    const newShadow = {
      blur: {
        type: 'CssUnitValue',
        unit: currentShadow.blur?.unit ?? 'px',
        value: newValue,
      } as CssUnitValue,
    } as Partial<Shadow>;
    handleShadowChange(
      newShadow,
      {
        controlId: 'shadow_blur',
        controlType: ControlTypes.slider,
        newValue,
        prevValue: currentShadow.blur?.value,
      },
      true,
    );
  };

  const handleSizeChange = (newValue: number) => {
    const newShadow = {
      size: {
        type: 'CssUnitValue',
        unit: currentShadow.size?.unit ?? 'px',
        value: newValue,
      } as CssUnitValue,
    } as Partial<Shadow>;
    handleShadowChange(
      newShadow,
      {
        controlId: 'shadow_size',
        controlType: ControlTypes.slider,
        newValue,
        prevValue: currentShadow.size?.value,
      },
      true,
    );
  };

  return (
    <>
      {!layersMode && (
        <Composites.ToggleSwitch>
          <ToggleSwitch
            label={TranslationKeys.shadows.toggleLabel}
            shouldTranslate={true}
            value={showShadow}
            onChange={handleShadowPanelShow}
            dataHook={DataHooks.shadowMode}
            className={styles.label}
          />
        </Composites.ToggleSwitch>
      )}
      {(showShadow || layersMode) && (
        <>
          {layersMode && (
            <Composites.ButtonsGroupLabeled>
              <TextLabel
                value={TranslationKeys.shadows.shadowType}
                shouldTranslate={true}
              />
              <ButtonsGroup
                value={
                  currentShadow?.inset
                    ? SHADOW_INSET_OPTIONS[1].value
                    : SHADOW_INSET_OPTIONS[0].value
                }
                options={SHADOW_INSET_OPTIONS}
                onChange={handleInsetChange}
              />
            </Composites.ButtonsGroupLabeled>
          )}
          <Composites.AngleInputLabeled>
            <TextLabel
              value={TranslationKeys.shadows.directionInputLabel}
              shouldTranslate={true}
            />
            <AngleInput
              value={currentShadow?.angle || 0}
              dataHook={DataHooks.shadowAngle}
              onChange={handleAngleChange}
            />
          </Composites.AngleInputLabeled>
          <Composites.ColorPickerInputLabeled>
            <TextLabel
              value={TranslationKeys.shadows.colorOpacityLabel}
              shouldTranslate={true}
            />
            <Composites.ColorPickerInput>
              <OpacityPicker
                value={(getAlpha(currentShadow.color) || 1) * 100}
                dataHook={DataHooks.shadowOpacity}
                onChange={handleOpacityChange}
                opacityGradientColor={getCssColor(
                  currentShadow?.color || '',
                  siteColorMap,
                )}
              />
              <ColorPickerInput
                value={getCssColor(currentShadow?.color || '', siteColorMap)}
                opacity={(getAlpha(currentShadow.color) || 1) * 100}
                onClick={handleShadowColorClick}
                dataHook={DataHooks.shadowColor}
              />
            </Composites.ColorPickerInput>
          </Composites.ColorPickerInputLabeled>
          <Composites.SliderLabeled>
            <TextLabel
              value={TranslationKeys.shadows.distanceInputLabel}
              shouldTranslate={true}
            />
            <Slider
              value={currentShadow.distance?.value || 0}
              dataHook={DataHooks.shadowDistance}
              onChange={handleDistanceChange}
              max={20}
              unit={currentShadow.distance?.unit ?? 'px'}
            />
          </Composites.SliderLabeled>
          <Composites.SliderLabeled>
            <TextLabel
              value={TranslationKeys.shadows.blurInputLabel}
              shouldTranslate={true}
            />
            <Slider
              value={currentShadow?.blur?.value || 0}
              dataHook={DataHooks.shadowBlur}
              onChange={handleBlurChange}
              max={40}
              unit={currentShadow.blur?.unit ?? 'px'}
            />
          </Composites.SliderLabeled>
          {layersMode && (
            <Composites.SliderLabeled>
              <TextLabel
                value={TranslationKeys.shadows.sizeInputLabel}
                shouldTranslate={true}
              />
              <Slider
                value={currentShadow?.size?.value || 0}
                dataHook={DataHooks.shadowSize}
                onChange={handleSizeChange}
                max={40}
                unit={currentShadow.size?.unit ?? 'px'}
              />
            </Composites.SliderLabeled>
          )}
        </>
      )}
    </>
  );
};
